import FindConversationJsonResponse from 'owa-service/lib/contract/FindConversationJsonResponse';
import FindFolderJsonResponse from 'owa-service/lib/contract/FindFolderJsonResponse';
import FindFolderResponseMessage from 'owa-service/lib/contract/FindFolderResponseMessage';
import SingleResponseMessage from 'owa-service/lib/contract/SingleResponseMessage';
import FindItemJsonResponse from 'owa-service/lib/contract/FindItemJsonResponse';
import FindItemResponseMessage from 'owa-service/lib/contract/FindItemResponseMessage';
import FindConversationResponseMessage from 'owa-service/lib/contract/FindConversationResponseMessage';
import { SessionData } from 'owa-service/lib/types/SessionData';
import ServerVersionInfo from 'owa-service/lib/contract/ServerVersionInfo';
import { addBottleneck } from 'owa-performance';

let findConversationResponse: FindConversationJsonResponse | undefined;
let findFoldersJsonResponse: FindFolderJsonResponse | undefined;
let findMailFolderItemResponse: FindItemJsonResponse | undefined;
let exchangeServerVersionInfo: ServerVersionInfo | undefined;

type BottleneckReason =
    | 'njr' // no json response
    | 'nrm' // no response message
    | 'wf' // wrong folder
    | 'wi' // wrong index
    | 'ni' // no items
    | 'e' // error response message
    | 's'; // success

export function getExchangeServerVersionInfo(): ServerVersionInfo | undefined {
    return exchangeServerVersionInfo;
}

export function stashSessionDataResponses(sessionData: SessionData) {
    findConversationResponse = sessionData.findConversation;
    findMailFolderItemResponse = sessionData.findMailFolderItem;
    findFoldersJsonResponse = sessionData.findFolders;
    exchangeServerVersionInfo = findFoldersJsonResponse?.Header?.ServerVersionInfo;
}

export function tryConsumeFindConversationResponse(
    folderId: string,
    startIndex: number
): FindConversationResponseMessage | null {
    const bottleneck = 'fcr';
    const responseMessage: FindConversationResponseMessage | null = extractSingleResponseMessage(
        findConversationResponse,
        r => r.Body,
        bottleneck
    );

    // Always clear out the stash when consuming findConversation response from session data regardless of the folderId.
    // even if we're not requesting inbox. We want to get a fresh response when user navigates to Inbox later
    findConversationResponse = undefined;

    if (responseMessage) {
        let reason: BottleneckReason | undefined;
        if (folderId !== responseMessage.FolderId?.Id) {
            reason = 'wf';
        } else if (!responseMessage.Conversations) {
            reason = 'ni';
        } else if (
            typeof responseMessage.IndexedOffset !== 'number' ||
            startIndex !== responseMessage.Conversations.length - responseMessage.IndexedOffset
        ) {
            reason = 'wi';
        }
        addBottleneck(bottleneck, reason || 's');
        if (!reason) {
            return responseMessage;
        }
    }

    return null;
}

export function tryConsumeFindMailFolderItemResponse(
    folderId: string,
    startIndex: number,
    inboxFolderId: string
): FindItemResponseMessage | null {
    const bottleneck = 'fir';
    const responseMessage: FindItemResponseMessage | null = extractSingleResponseMessage(
        findMailFolderItemResponse,
        r => r?.Body?.ResponseMessages?.Items?.[0],
        bottleneck
    );

    // Always clear out the stash when consuming findItem response from session data regardless of the folderId.
    // even if we're not requesting inbox. We want to get a fresh response when user navigates to Inbox later
    findMailFolderItemResponse = undefined;

    if (responseMessage) {
        let reason: BottleneckReason | undefined;
        const rootFolder = responseMessage?.RootFolder;
        if (folderId !== inboxFolderId) {
            reason = 'wf';
        } else if (!rootFolder?.Items) {
            reason = 'ni';
        } else if (
            typeof rootFolder.IndexedPagingOffset !== 'number' ||
            startIndex !== rootFolder.Items.length - rootFolder.IndexedPagingOffset
        ) {
            reason = 'wi';
        }
        addBottleneck(bottleneck, reason || 's');
        if (!reason) {
            return responseMessage;
        }
    }

    return null;
}

/**
 * The session data findFolders response shall be consumed when OWA boots
 * and when primary account folders load. After that the session data findFolders
 * response gets set to null and hence there shall be no issues of this response
 * getting accidentally consumed for connected accounts which load only on demand.
 */
export function tryConsumeFindFoldersResponse(): FindFolderResponseMessage | null {
    const bottleneck = 'ffr';
    const findFolderResponseMessage: FindFolderResponseMessage | null = extractSingleResponseMessage(
        findFoldersJsonResponse,
        r => r?.Body?.ResponseMessages?.Items?.[0],
        bottleneck
    );
    // Clear out the response after consuming it
    findFoldersJsonResponse = undefined;
    if (findFolderResponseMessage) {
        addBottleneck(bottleneck, 's');
    }
    return findFolderResponseMessage;
}

function extractSingleResponseMessage<T>(
    jsonResponse: T,
    extract: (data: T) => SingleResponseMessage,
    bottleneck: string
) {
    let bottleneckReason: BottleneckReason = 'njr';
    if (jsonResponse) {
        const singleResponseMessage: SingleResponseMessage = extract(jsonResponse);
        if (!singleResponseMessage) {
            bottleneckReason = 'nrm';
        } else if (singleResponseMessage.ResponseClass === 'Error') {
            bottleneckReason = 'e';
            addBottleneck(bottleneck + '_c', singleResponseMessage.ResponseCode || 'un');
        } else {
            return singleResponseMessage;
        }
    }
    addBottleneck(bottleneck, bottleneckReason);
    return null;
}
